import React, { useState, useEffect } from 'react';
import './WorkCalendar.css';

// Интерфейс для исключенных дней
interface ExcludedDay {
    date: string; // формат YYYY-MM-DD
    reason: string;
}

const WorkCalendar: React.FC = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarDays, setCalendarDays] = useState<Array<{
        date: Date;
        isCurrentMonth: boolean;
        isWorkDay: boolean;
        isWorkOneThree: boolean;
        isWorkTwoTwo: boolean;
        isWeekend: boolean;
        isWeekendOff: boolean;
        isPartyDay: boolean;
        isExcluded: boolean;
        excludedReason: string;
        wouldBeWeekendOff: boolean;
    }>>([]);

    // Список исключенных дней
    const excludedDays: ExcludedDay[] = [
        { date: '2025-04-17', reason: 'Саша перепил на корпоративе и сказал Дмитрию "Не называйте меня так..."' },
        { date: '2025-04-25', reason: 'Дима в Волгограде' },
    ];

    // Функция для проверки, является ли день исключенным
    const isExcludedDay = (date: Date): { isExcluded: boolean, reason: string } => {
        const dateString = date.toISOString().split('T')[0]; // получаем формат YYYY-MM-DD
        const excludedDay = excludedDays.find(day => day.date === dateString);

        if (excludedDay) {
            return { isExcluded: true, reason: excludedDay.reason };
        }
        return { isExcluded: false, reason: '' };
    };

    // Функция для определения рабочих дней по графику 1/3
    const isWorkingDayOneThree = (date: Date): boolean => {
        const knownWorkDayOneThree = new Date(2025, 3, 23);
        const dayDiffOneThree = Math.floor((date.getTime() - knownWorkDayOneThree.getTime()) / (1000 * 3600 * 24));
        const modOneThree = ((dayDiffOneThree % 4) + 4) % 4; // нормализуем для отрицательных значений
        return modOneThree === 0;
    };

    // Функция для определения рабочих дней по графику 2/2
    const isWorkingDayTwoTwo = (date: Date): boolean => {
        const knownWorkStartTwoTwo = new Date(2025, 7, 20); // 20.08.2025
        const dayDiffTwoTwo = Math.floor((date.getTime() - knownWorkStartTwoTwo.getTime()) / (1000 * 3600 * 24));
        const modTwoTwo = ((dayDiffTwoTwo % 4) + 4) % 4; // 0-1 работа, 2-3 выходные
        return modTwoTwo === 0 || modTwoTwo === 1;
    };

    // Функция для определения, является ли день рабочим
    const isWorkingDay = (date: Date, checkExclusions: boolean = true): boolean => {
        const isWorkOneThree = isWorkingDayOneThree(date);
        const isWorkTwoTwo = isWorkingDayTwoTwo(date);

        // День считается рабочим, если он рабочий по ЛЮБОМУ из графиков
        return isWorkOneThree || isWorkTwoTwo;
    };

    // Функция для определения, является ли день "тусовочным"
    const checkIfPartyDay = (date: Date): boolean => {
        // Проверяем, что день - суббота или воскресенье
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;

        // Проверяем, что день не рабочий по графику (без учета исключений)
        const isNotWorkDay = !isWorkingDay(date, false);

        // Проверяем, что следующий день - рабочий
        const nextDay = new Date(date);
        nextDay.setDate(date.getDate() + 1);
        const isNextDayWork = isWorkingDay(nextDay);

        return isWeekend && isNotWorkDay && isNextDayWork;
    };

    // Создание массива дней для календаря
    const generateCalendarDays = (year: number, month: number) => {
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const daysInMonth = lastDayOfMonth.getDate();

        // Определяем день недели первого дня месяца (0 - воскресенье, 1 - понедельник, и т.д.)
        let startingDayOfWeek = firstDayOfMonth.getDay();
        // Преобразуем, чтобы неделя начиналась с понедельника
        startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

        const days = [];

        // Добавляем дни предыдущего месяца
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevMonthYear = month === 0 ? year - 1 : year;
        const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

        for (let i = 0; i < startingDayOfWeek; i++) {
            const day = daysInPrevMonth - startingDayOfWeek + i + 1;
            const date = new Date(prevMonthYear, prevMonth, day);

            const { isExcluded, reason } = isExcludedDay(date);
            const isWorkOneThree = isWorkingDayOneThree(date);
            const isWorkTwoTwo = isWorkingDayTwoTwo(date);
            const isWorkDay = isWorkOneThree || isWorkTwoTwo;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            const wouldBeWorkDay = isWorkingDay(date, false);
            const isWeekendOff = isWeekend && !wouldBeWorkDay;
            const wouldBeWeekendOff = isWeekend && !wouldBeWorkDay;

            const isPartyDay = checkIfPartyDay(date);

            days.push({
                date,
                isCurrentMonth: false,
                isWorkDay,
                isWorkOneThree,
                isWorkTwoTwo,
                isWeekend,
                isWeekendOff,
                isPartyDay,
                isExcluded,
                excludedReason: reason,
                wouldBeWeekendOff
            });
        }

        // Добавляем дни текущего месяца
        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month, i);

            const { isExcluded, reason } = isExcludedDay(date);
            const isWorkOneThree = isWorkingDayOneThree(date);
            const isWorkTwoTwo = isWorkingDayTwoTwo(date);
            const isWorkDay = isWorkOneThree || isWorkTwoTwo;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            const wouldBeWorkDay = isWorkingDay(date, false);
            const isWeekendOff = isWeekend && !wouldBeWorkDay;
            const wouldBeWeekendOff = isWeekend && !wouldBeWorkDay;

            const isPartyDay = checkIfPartyDay(date);

            days.push({
                date,
                isCurrentMonth: true,
                isWorkDay,
                isWorkOneThree,
                isWorkTwoTwo,
                isWeekend,
                isWeekendOff,
                isPartyDay,
                isExcluded,
                excludedReason: reason,
                wouldBeWeekendOff
            });
        }

        // Добавляем дни следующего месяца до конца недели
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextMonthYear = month === 11 ? year + 1 : year;
        const remainingDays = 42 - days.length; // 6 недель по 7 дней

        for (let i = 1; i <= remainingDays; i++) {
            const date = new Date(nextMonthYear, nextMonth, i);

            const { isExcluded, reason } = isExcludedDay(date);
            const isWorkOneThree = isWorkingDayOneThree(date);
            const isWorkTwoTwo = isWorkingDayTwoTwo(date);
            const isWorkDay = isWorkOneThree || isWorkTwoTwo;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            const wouldBeWorkDay = isWorkingDay(date, false);
            const isWeekendOff = isWeekend && !wouldBeWorkDay;
            const wouldBeWeekendOff = isWeekend && !wouldBeWorkDay && isExcluded;

            const isPartyDay = checkIfPartyDay(date);

            days.push({
                date,
                isCurrentMonth: false,
                isWorkDay,
                isWorkOneThree,
                isWorkTwoTwo,
                isWeekend,
                isWeekendOff,
                isPartyDay,
                isExcluded,
                excludedReason: reason,
                wouldBeWeekendOff
            });
        }

        return days;
    };

    useEffect(() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        setCalendarDays(generateCalendarDays(currentYear, currentMonth));
    }, [currentDate]);

    const goToPreviousMonth = () => {
        setCurrentDate(prevDate => {
            const prevMonth = prevDate.getMonth() === 0 ? 11 : prevDate.getMonth() - 1;
            const prevYear = prevDate.getMonth() === 0 ? prevDate.getFullYear() - 1 : prevDate.getFullYear();
            return new Date(prevYear, prevMonth, 1);
        });
    };

    const goToNextMonth = () => {
        setCurrentDate(prevDate => {
            const nextMonth = prevDate.getMonth() === 11 ? 0 : prevDate.getMonth() + 1;
            const nextYear = prevDate.getMonth() === 11 ? prevDate.getFullYear() + 1 : prevDate.getFullYear();
            return new Date(nextYear, nextMonth, 1);
        });
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const formatMonth = (date: Date): string => {
        return date.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    };

    const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

    // Определяем CSS класс для дня в зависимости от графика
    const getDayClass = (day: any) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPast = day.date < today;

        if (isPast) return 'past-day';
        if (day.isWeekendOff) return 'weekend-off';
        if (!day.isWorkDay) return 'day-off';

        // Для рабочих дней определяем по какому графику
        if (day.isWorkOneThree && day.isWorkTwoTwo) {
            return 'work-day-both'; // Пересечение графиков
        } else if (day.isWorkOneThree) {
            return 'work-day-1-3'; // Только график 1/3
        } else if (day.isWorkTwoTwo) {
            return 'work-day-2-2'; // Только график 2/2
        }

        return 'day-off';
    };

    return (
        <div className="work-calendar">
            <h1>Рабочий календарь</h1>
            <div className="calendar-legend">
                <div className="legend-item">
                    <div className="legend-color work-day-1-3"></div>
                    <span>График 1/3</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color work-day-2-2"></div>
                    <span>График 2/2</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color work-day-both"></div>
                    <span>Пересечение графиков</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color day-off"></div>
                    <span>Выходной</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color weekend-off"></div>
                    <span>Выходной + Выходные</span>
                </div>
                <div className="legend-item">
                    <span className="warning-emoji">⚠️</span>
                    <span>Исключение</span>
                </div>
            </div>
            <div className="calendar-header">
                <button onClick={goToPreviousMonth}>&#8592;</button>
                <h2>{formatMonth(currentDate)}</h2>
                <button onClick={goToNextMonth}>&#8594;</button>
                <button onClick={goToToday} className="today-btn">Сегодня</button>
            </div>
            <div className="calendar-grid">
                {weekdays.map(day => (
                    <div key={day} className="weekday">{day}</div>
                ))}
                {calendarDays.map((day, index) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isPast = day.date < today;

                    return (
                        <div
                            key={index}
                            className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${
                                getDayClass(day)
                            } ${
                                new Date().toDateString() === day.date.toDateString() ? 'today' : ''
                            }`}
                            title={!isPast && day.excludedReason ? `Исключение: ${day.excludedReason}` : ''}
                        >
                            {day.date.getDate()}

                            {!isPast && day.isExcluded && (
                                <span className="warning-emoji" title={`Исключение: ${day.excludedReason}`}>⚠️</span>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="schedule-info">
                <h3>Графики работы:</h3>
                <p><strong>График 1/3:</strong> сутки через трое (якорная дата: 23 апреля 2025)</p>
                <p><strong>График 2/2:</strong> две рабочие, две выходные (якорные дни: 20-21 августа 2025)</p>
                <p>На зеленом фоне отмечены дни, когда выходные по графику совпадают с выходными днями.</p>
                <p>Рабочие дни выделены разными цветами в зависимости от графика.</p>
                <p><span>⚠️</span> отмечены дни-исключения.</p>
            </div>
        </div>
    );
};

export default WorkCalendar;